import {
  format as dfFormat,
  differenceInYears,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInSeconds,
  subMonths,
  addYears,
} from "date-fns";

/**
 * moment → date-fns compatibility layer (2026-07 polish pass).
 *
 * The app used moment/moment-timezone (~230KB) for a small, finite API
 * surface: 18 display-format patterns, diff() in six units, UTC rendering,
 * a same-calendar-day check, elapsed-time maths, and the local timezone
 * abbreviation. Everything here preserves moment's exact output for those
 * usages (locked in by src/Utils/dateFormat.test.js) using date-fns +
 * native Intl.
 *
 * Call sites keep passing their original MOMENT pattern strings; the map
 * below translates them. Add new patterns here (with a test) — do not pass
 * date-fns tokens directly.
 */

// moment pattern → date-fns pattern. Case matters:
//   moment YYYY/DD/D  → date-fns yyyy/dd/d
//   moment A (AM/PM)  → date-fns a
//   moment a (am/pm)  → date-fns aaa
const MOMENT_TO_DATEFNS = {
  "MMM D, YYYY h:mma": "MMM d, yyyy h:mmaaa",
  "MMM DD, YYYY hh:mm A": "MMM dd, yyyy hh:mm a",
  "MMM DD YYYY, hh:mm A": "MMM dd yyyy, hh:mm a",
  "MMM D, YYYY": "MMM d, yyyy",
  "hh:mm A": "hh:mm a",
  "DD/MM/YYYY": "dd/MM/yyyy",
  "MMM DD, YYYY h:mma": "MMM dd, yyyy h:mmaaa",
  "MMM DD,YYYY h:mma": "MMM dd,yyyy h:mmaaa",
  "MMM DD, YYYY": "MMM dd, yyyy",
  "MMM DD YYYY hh:mm A": "MMM dd yyyy hh:mm a",
  "MMM D, YYYY hh:mm A": "MMM d, yyyy hh:mm a",
  "MMM D YYYY hh:mm A": "MMM d yyyy hh:mm a",
  "MM/DD/YY hh:mm a": "MM/dd/yy hh:mm aaa",
  "MM-DD-YYYY": "MM-dd-yyyy",
  "MM-DD-yyyy": "MM-dd-yyyy",
  "DD-MM-YYYY": "dd-MM-yyyy",
  "MMM, DD YYYY hh:mm A": "MMM, dd yyyy hh:mm a",
  "MMM D, YYYY h:mm A": "MMM d, yyyy h:mm a",
  "MMMM Do YYYY, h:mm:ss A": "MMMM do yyyy, h:mm:ss a",
};

/**
 * moment(value)-compatible coercion to Date for the value shapes this app
 * passes: Date, epoch-ms number, epoch-ms numeric string, ISO string,
 * null/undefined (→ now). NOT a general moment parser — e.g. compact
 * "20260701" date strings would be treated as epoch ms, unlike moment.
 */
export function toDate(value) {
  if (value instanceof Date) return value;
  if (typeof value === "number") return new Date(value);
  if (typeof value === "string" && /^\d+$/.test(value.trim())) {
    return new Date(Number(value.trim()));
  }
  if (value === undefined || value === null) return new Date();
  return new Date(value);
}

/** Replacement for moment(value).format(momentPattern). */
export function formatDate(value, momentPattern) {
  const pattern = MOMENT_TO_DATEFNS[momentPattern];
  if (!pattern) {
    throw new Error(
      `dateFormat: unmapped moment pattern "${momentPattern}" — add it to MOMENT_TO_DATEFNS with a test.`
    );
  }
  const date = toDate(value);
  if (isNaN(date.getTime())) return "Invalid date"; // moment's output for bad input
  return dfFormat(date, pattern);
}

/** Replacement for moment.utc(value).format(momentPattern). */
export function formatDateUTC(value, momentPattern) {
  const date = toDate(value);
  if (isNaN(date.getTime())) return "Invalid date";
  // Shift by the local offset so the wall-clock fields read as UTC.
  const shifted = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
  return formatDate(shifted, momentPattern);
}

/** Replacement for moment(a).diff(b, unit) — truncates toward zero like moment. */
export function diffDates(a, b, unit) {
  const left = toDate(a);
  const right = toDate(b);
  switch (unit) {
    case "years":
      return differenceInYears(left, right);
    case "days":
      return differenceInDays(left, right);
    case "hours":
      return differenceInHours(left, right);
    case "minutes":
      return differenceInMinutes(left, right);
    case "seconds":
      return differenceInSeconds(left, right);
    default:
      throw new Error(`diffDates: unsupported unit "${unit}"`);
  }
}

/** Replacement for moment(a).get("date") === moment(b).get("date") day checks. */
export function dayOfMonth(value) {
  return toDate(value).getDate();
}

/**
 * Replacement for moment.duration(value, unit?). Accepts milliseconds
 * (default, like moment) or an explicit unit ("seconds"), and exposes the
 * accessors the app uses: asHours/asMinutes (fractional totals) and
 * hours/minutes/seconds (clock-style components, like moment's).
 */
export function durationParts(value, unit = "milliseconds") {
  const ms = unit === "seconds" ? value * 1000 : value;
  // Math.trunc, not Math.floor: moment truncates components toward zero,
  // which differs for negative durations (e.g. clock skew).
  const totalSeconds = Math.trunc(ms / 1000);
  const totalMinutes = Math.trunc(ms / 60000);
  const noNegZero = (n) => (n === 0 ? 0 : n);
  return {
    asHours: () => ms / 3600000,
    asMinutes: () => ms / 60000,
    hours: () => noNegZero(Math.trunc(ms / 3600000) % 24),
    minutes: () => noNegZero(totalMinutes % 60),
    seconds: () => noNegZero(totalSeconds % 60),
  };
}

/**
 * moment(x).format(...) round-trips through "DD-MM-YYYY" were used to
 * truncate a date-of-birth to local midnight before age diffs. This is the
 * explicit equivalent.
 */
export function startOfLocalDay(value) {
  const d = toDate(value);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Replacement for moment(x).subtract(n, "months").valueOf(). */
export function subtractMonthsMs(value, n) {
  return subMonths(toDate(value), n).getTime();
}

/**
 * Replacement for `moment(to).diff(from, "years", true) > n` range checks:
 * true when `to` is after `from + n calendar years`.
 */
export function isMoreThanYearsApart(from, to, years) {
  return toDate(to).getTime() > addYears(toDate(from), years).getTime();
}

/**
 * Replacement for moment-timezone's
 * moment().tz(zone).format("MMM DD YYYY, hh:mm A (z)") — renders a moment
 * pattern's wall clock in an explicit IANA zone via native Intl. Only the
 * patterns used by the app are supported.
 */
export function formatInTimeZone(value, timeZone, momentPattern) {
  const date = toDate(value);
  if (isNaN(date.getTime())) return "Invalid date";
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZoneName: "short",
  }).formatToParts(date);
  const get = (type) => (parts.find((p) => p.type === type) || {}).value || "";
  if (momentPattern === "MMM DD YYYY, hh:mm A (z)") {
    const hour = get("hour").padStart(2, "0");
    return `${get("month")} ${get("day")} ${get("year")}, ${hour}:${get(
      "minute"
    )} ${get("dayPeriod").toUpperCase()} (${get("timeZoneName")})`;
  }
  throw new Error(
    `formatInTimeZone: unmapped moment pattern "${momentPattern}"`
  );
}

/**
 * Replacement for moment-timezone's tz.guess() + .format("z"): the local
 * timezone abbreviation ("PST", "EDT", …) via native Intl.
 */
export function localTimeZoneAbbr(date = new Date()) {
  const part = new Intl.DateTimeFormat("en-US", {
    timeZoneName: "short",
  })
    .formatToParts(date)
    .find((p) => p.type === "timeZoneName");
  return part ? part.value : "";
}
